import React, { useContext, useEffect, useMemo, useState, useRef } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Button,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Textarea,
  useToast,
  Select,
  Icon,
  Spinner,
} from '@chakra-ui/react';
import { FaPlus, FaMinus, FaInstagram, FaTiktok, FaGlobe, FaCoffee, FaGift, FaMoneyBillWave, FaCheck } from 'react-icons/fa';
import { FiMapPin, FiEdit2 } from 'react-icons/fi';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import DeleteRequestModal from '../../collabs/components/DeleteRequestModal';
import { Campaign, CampaignType, Client, Collab, SocialChannel, InfluencerLocation } from '@foodfluence/shared';
import { getChannelIcon } from '../../../utils/channelUtils';
import { campaignTypeDisplayNames } from '../../../types';
import { InfluencerDataContext } from 'contexts/InfluencerDataContext';
import { getInfluencerPrimaryHandle, getMissingDeliverableHandles, isCreatorUGCOnly } from '../../../utils/influencerUtils';
import { updateInfluencerHandles } from '../../../services/flaskService';
import MissingHandleInputs, { MissingHandleKey, MISSING_HANDLE_LABELS } from 'components/MissingHandleInputs';
import { getUgcPresetCounts, applyPresetCountsToRequirements, ugcDeliverableMatch } from '../../../utils/contentUtils';
import UgcDeliverablesPicker, { UgcLogisticsPicker } from './UgcDeliverablesPicker';
import ProposalReviewSummary from './ProposalReviewSummary';
import UgcCampaignApplySummary from './UgcCampaignApplySummary';
import { PitchType } from './PitchCollabSheet';
import CreativeBrief from 'features/collabs/components/creative-brief/CreativeBrief';
import { collabApi } from 'services/api/collab';
import LocationList from 'components/LocationList';
import InfluencerLocationPicker from 'components/InfluencerLocationPicker';
import { isInternalClient } from 'utils/clientUtils';
import { getInboundLinkToken } from 'utils/inboundLinkUtils';
import { CollabDataContext } from 'contexts/collabDataContext';
import { InfluencerCampaignDataContext } from 'contexts/InfluencerCampaignDataContext';
import {
  SINGLE_CLIENT_COLLAB_REQUEST_LIMIT,
  hasCollabChanges,
  isCollabValid,
  findExistingCollabRequest,
  getPendingCollabRequestsForClient,
  generateCollab
} from '../utils';
import { CollabFormValues } from '../types';
import { useClientLocations } from 'hooks/useClientLocations';
import { useCampaignLocations } from 'hooks/useCampaignLocations';
import { useInfluencerLocations } from 'contexts/InfluencerLocationContext';
import { isLocationfulCampaignType, isInStoreCampaignType, getCollabAvailableTimes } from 'utils/collabUtils';
import { getClosestLocations, getInfluencerPrimaryLocation } from 'utils/locationUtils';
import PostByConfirm, { usePostByConfirm } from 'features/collabs/components/PostByConfirm';
import FrostedSupportButton from 'components/FrostedSupportButton';
import { ff } from '../../../theme';

export interface RequestCollabFormProps {
  campaign: Campaign | null;
  client: Client | null;
  /** Explicitly supplied existing request (e.g. from navigation state or a pending-request card). */
  requestedCollab?: Collab | null;
  /** Called when the user leaves the form: back button, successful submit, or request deletion. */
  onExit: () => void;
  /** Rendered inside a modal — skips the page-level scroll reset. */
  inModal?: boolean;
  /** Custom requests: what the creator chose in the Pitch a collab sheet. */
  pitchType?: PitchType;
  /** Scroll the compensation inputs into view on open (the campaign page's "Counter-offer"). */
  scrollToCompensation?: boolean;
  /** Campaign locations pre-picked on the campaign page (ignored when updating an existing request). */
  initialSelectedLocationIds?: string[];
}

const SectionHeading: React.FC<{ children: React.ReactNode; right?: React.ReactNode }> = ({ children, right }) => (
  <HStack justify="space-between" mb={2.5}>
    <Text textStyle="display" fontSize="18px" color={ff.ink}>{children}</Text>
    {right && <Text fontSize="13px" color={ff.text2}>{right}</Text>}
  </HStack>
);

const RequestCollabForm: React.FC<RequestCollabFormProps> = ({
  campaign = null,
  client = null,
  requestedCollab: requestedCollabProp = null,
  onExit,
  inModal = false,
  pitchType,
  scrollToCompensation = false,
  initialSelectedLocationIds,
}) => {
  const compensationRef = useRef<HTMLDivElement>(null);

  const toast = useToast();
  const toastShown = useRef(false);
  const {influencerData, loading: isInfluencerDataLoading} = useContext(InfluencerDataContext);
  const collabData = useContext(CollabDataContext);
  const campaigns = useContext(InfluencerCampaignDataContext);

  const { locations: clientLocations, loading: isLoadingClientLocations, error: clientLocationsError } = useClientLocations(client?.id);

  // For review campaigns, get campaign locations (not used directly but needed for CreativeBrief)
  const { locations: campaignLocations, loading: isLoadingCampaignLocations } = useCampaignLocations(campaign?.foodfluenceLocations);


  const isCustomRequest = !campaign;

  const clientCampaignTypes = client?.campaignTypes || ['inStore'];


  const pendingRequests = getPendingCollabRequestsForClient(collabData, campaigns, client);

  let requestedCollab: Collab | null = requestedCollabProp || findExistingCollabRequest(campaign, pendingRequests);

  const [tikTokCount, setTikTokCount] = useState(requestedCollab?.proposedInfluencerDeliverableCounts?.tiktoks ?? campaign?.deliverableCounts?.tiktoks ?? 0);
  const [igReelCount, setIgReelCount] = useState(requestedCollab?.proposedInfluencerDeliverableCounts?.instagramReels ?? campaign?.deliverableCounts?.instagramReels ?? 0);
  const [igPostCount, setIgPostCount] = useState(requestedCollab?.proposedInfluencerDeliverableCounts?.instagramPosts ?? campaign?.deliverableCounts?.instagramPosts ?? 0);
  const [igStoryCount, setIgStoryCount] = useState(requestedCollab?.proposedInfluencerDeliverableCounts?.instagramStories ?? campaign?.deliverableCounts?.instagramStories ?? 0);
  const [cashAmount, setCashAmount] = useState(requestedCollab?.proposedInfluencerCash ?? campaign?.cash ?? 0);
  const [creditAmount, setCreditAmount] = useState(requestedCollab?.proposedInfluencerCredit ?? campaign?.credit ?? 0);
  const [note, setNote] = useState(requestedCollab?.influencerNote || '');
  const [campaignType, setCampaignType] = useState<CampaignType>(requestedCollab?.customCollabType ?? campaign?.campaignType ?? clientCampaignTypes[0]);
  const [proposedSelectedLocationIds, setProposedSelectedLocationIds] = useState<string[]>(
    requestedCollab?.proposedSelectedLocationIds ?? initialSelectedLocationIds ?? []
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedInfluencerLocation, setSelectedInfluencerLocation] = useState<InfluencerLocation | null>(null);
  const { influencerLocations } = useInfluencerLocations();

  const isCustomBrandManaged = isCustomRequest && campaignType === 'brandManaged';
  // Go-live campaigns: sending or editing a proposal needs "I can post on …" ticked.
  const postBy = usePostByConfirm(campaign);

  const [handleInputs, setHandleInputs] = useState<Record<MissingHandleKey, string>>({ instagramHandle: '', tiktokHandle: '' });
  const missingHandles = getMissingDeliverableHandles(influencerData, {
    tiktoks: tikTokCount,
    instagramReels: igReelCount,
    instagramPosts: igPostCount,
    instagramStories: igStoryCount,
  });
  const handlesComplete = missingHandles.every((key) => handleInputs[key].trim() !== '');

  // UGC proposals deliver content pieces instead of social posts. The creator picks the type in the
  // Pitch a collab sheet; an existing request keeps its type; otherwise UGC-only creators pitch UGC
  const isUgcProposal = isCustomRequest && (
    pitchType
      ? pitchType === 'ugc'
      : requestedCollab
        ? requestedCollab.creatorType === 'ugc' || Object.keys(requestedCollab.proposedContent ?? {}).length > 0
        : isCreatorUGCOnly(influencerData)
  );
  // Applying to a UGC campaign: its terms are fixed, so one summary screen and Apply
  const isUgcCampaignApply = !isCustomRequest && Object.keys(campaign?.contentRequirements ?? {}).length > 0;
  // Same option set as the influencer Collab Type select: the brand's supported types,
  // with shipToHome dropped when shopify exists (both read "Ship to Home" to creators)
  const ugcCollabTypeOptions = clientCampaignTypes.filter(type =>
    !(type === 'shipToHome' && clientCampaignTypes.includes('shopify'))
  );
  const [ugcPresetCounts, setUgcPresetCounts] = useState<Record<string, number>>(
    () => getUgcPresetCounts(requestedCollab?.proposedContent)
  );
  const [cashEdited, setCashEdited] = useState<boolean>(!!requestedCollab);

  // Custom proposals: build the request on one screen, then review & submit
  const isMultiStep = isCustomRequest;
  const STEP_TITLES = ['Build your request', 'Review & submit'];
  const REVIEW_STEP = STEP_TITLES.length - 1;
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const showStep = (step: number) => !isMultiStep || currentStep === step;
  const formTopRef = useRef<HTMLDivElement>(null);
  // In the modal the modal scrolls, not the window (window would move the explore page behind it)
  const scrollFormToTop = () => {
    if (inModal) formTopRef.current?.scrollIntoView({ block: 'start' });
    else window.scrollTo(0, 0);
  };
  const goToStep = (step: number) => {
    setCurrentStep(step);
    scrollFormToTop();
  };

  const rateByPreset = useMemo(() => {
    return (influencerData?.ugcRateRanges ?? []).reduce<Record<string, number>>((rates, rate) => {
      const preset = ugcDeliverableMatch(rate);
      if (preset && rate.minRate > 0) {
        rates[preset.displayName] = rate.minRate;
      }
      return rates;
    }, {});
  }, [influencerData?.ugcRateRanges]);

  const suggestedUgcCash = useMemo(() => {
    return Object.entries(ugcPresetCounts).reduce(
      (total, [presetName, count]) => total + count * (rateByPreset[presetName] || 0),
      0
    );
  }, [ugcPresetCounts, rateByPreset]);

  // influencerData loads async; keep the selection within the brand's offered collab types.
  // New requests only — an existing proposal's stored type must never be silently rewritten.
  useEffect(() => {
    if (isUgcProposal && !requestedCollab && ugcCollabTypeOptions.length > 0 && !ugcCollabTypeOptions.includes(campaignType)) {
      setCampaignType(ugcCollabTypeOptions[0]);
    }
  }, [isUgcProposal, campaignType, ugcCollabTypeOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tally cash from the creator's own rates until they type their own number
  useEffect(() => {
    if (!isUgcProposal || cashEdited) return;
    setCashAmount(suggestedUgcCash);
  }, [isUgcProposal, cashEdited, suggestedUgcCash]);

  // Auto-select primary (priority 1), or first location in list if none marked primary
  useEffect(() => {
    if (selectedInfluencerLocation || influencerLocations.length === 0) return;
    setSelectedInfluencerLocation(getInfluencerPrimaryLocation(influencerLocations));
  }, [influencerLocations]); // eslint-disable-line react-hooks/exhaustive-deps

  const closestLocations = getClosestLocations(
    clientLocations ?? [],
    selectedInfluencerLocation ? { lat: selectedInfluencerLocation.lat, lng: selectedInfluencerLocation.lng } : null,
    3
  );

  const formValues: CollabFormValues = {
    tikTokCount,
    igReelCount,
    igPostCount,
    igStoryCount,
    cashAmount,
    creditAmount,
    note,
    campaignType,
    isContentBased: campaign?.contentRequirements && Object.keys(campaign.contentRequirements).length > 0,
    ugcPresetCounts: isUgcProposal ? ugcPresetCounts : undefined
  };

  const handleLocationClick = (docId: string) => {
    setProposedSelectedLocationIds(prev =>
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  };

  const isDisabled = (): boolean => {
    const effectiveCampaignType = isCustomRequest ? campaignType : campaign?.campaignType;
    if (postBy.blockedReason) return true;
    if (isLocationfulCampaignType(effectiveCampaignType)) {
      return isSubmitting;
    }
    return !isCollabValid(formValues, requestedCollab, pendingRequests.length, campaign) ||
           isSubmitting ||
           !handlesComplete ||
           !hasCollabChanges(formValues, requestedCollab, isCustomRequest, proposedSelectedLocationIds);
  };

  const getLocationfulValidationErrors = (): string[] => {
    const effectiveCampaignType = isCustomRequest ? campaignType : campaign?.campaignType;
    const errors: string[] = [];

    if (proposedSelectedLocationIds.length === 0) {
      errors.push('at least one location');
    }

    if (isUgcProposal) {
      const hasUgcDeliverables = Object.values(ugcPresetCounts).some(count => count > 0);
      if (!hasUgcDeliverables) {
        errors.push('at least one deliverable');
      }
    } else if (!formValues.isContentBased) {
      const hasDeliverables = tikTokCount > 0 || igReelCount > 0 || igPostCount > 0 || igStoryCount > 0;
      if (!hasDeliverables) {
        errors.push('at least one deliverable');
      }
    }

    if (effectiveCampaignType === 'inStore' && cashAmount === 0 && creditAmount === 0) {
      errors.push('compensation (cash or credit)');
    }

    missingHandles
      .filter((key) => handleInputs[key].trim() === '')
      .forEach((key) => errors.push(`your ${MISSING_HANDLE_LABELS[key]} handle`));

    return errors;
  };

  const ugcPiecesSelected = Object.values(ugcPresetCounts).reduce((total, count) => total + count, 0);

  /** Why the build step can't continue yet (location, deliverables, handle, compensation), if anything. */
  const getStepBlocker = (step: number): string | null => {
    if (step !== 0) return null;
    if (isLocationfulCampaignType(campaignType) && proposedSelectedLocationIds.length === 0) {
      return 'Select at least one location to continue';
    }
    const hasDeliverables = isUgcProposal
      ? ugcPiecesSelected > 0
      : tikTokCount > 0 || igReelCount > 0 || igPostCount > 0 || igStoryCount > 0;
    if (!hasDeliverables) return 'Add at least one deliverable to continue';
    if (!handlesComplete) return 'Add your social handle to continue';
    const requiresCompensation = campaignType === 'inStore' || isCustomBrandManaged;
    if (requiresCompensation && cashAmount === 0 && creditAmount === 0) {
      return 'Add cash or credit to continue';
    }
    return null;
  };
  const currentStepBlocker = isMultiStep ? getStepBlocker(currentStep) : null;

  const handleIncrement = (setter: React.Dispatch<React.SetStateAction<number>>, value: number) => {
    setter(value + 1);
  };

  const handleDecrement = (setter: React.Dispatch<React.SetStateAction<number>>, value: number) => {
    if (value > 0) {
      setter(value - 1);
    }
  };

  const handleCashChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    if (!isNaN(value)) {
      setCashAmount(value);
      setCashEdited(true);
    }
  };

  const handleCreditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    if (!isNaN(value)) {
      setCreditAmount(value);
    }
  };

  const handleNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNote(e.target.value);
  };

  const handleSendRequest = async () => {
    const effectiveCampaignType = isCustomRequest ? campaignType : campaign?.campaignType;
    if (isLocationfulCampaignType(effectiveCampaignType)) {
      const errors = getLocationfulValidationErrors();
      if (errors.length > 0) {
        toast({
          title: 'Missing required fields',
          description: `Please fill out: ${errors.join(', ')}.`,
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
        return;
      }
    }

    setIsSubmitting(true);
    if (missingHandles.length > 0) {
      try {
        await updateInfluencerHandles(influencerData.id, handleInputs);
      } catch (error) {
        console.error('Error verifying social handles:', error);
        toast({
          title: "We couldn't verify that handle",
          description: "Please double-check your social media handle and try again.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setIsSubmitting(false);
        return;
      }
    }
    try {
      const deliverableCounts = {
        tiktoks: tikTokCount,
        instagramReels: igReelCount,
        instagramPosts: igPostCount,
        instagramStories: igStoryCount
      };

      const effectiveCampaignType = isCustomRequest ? campaignType : campaign?.campaignType;
      const locationIds = isLocationfulCampaignType(effectiveCampaignType) && proposedSelectedLocationIds.length > 0
        ? proposedSelectedLocationIds
        : undefined;

      // Derived from the stored proposal so piece ids stay stable across edits (the brand's accept
      // brief is keyed by them); a new request starts from nothing
      const proposedContent = isUgcProposal
        ? applyPresetCountsToRequirements(requestedCollab?.proposedContent ?? {}, ugcPresetCounts)
        : undefined;

      if (requestedCollab) {
        await collabApi.updateCollabRequest({
          collabId: requestedCollab.id,
          deliverableCounts,
          cash: cashAmount,
          credit: creditAmount,
          note: isCustomRequest || isUgcCampaignApply ? note : null,
          customCollabType: isCustomRequest ? campaignType : null,
          proposedSelectedLocationIds: locationIds,
          proposedContent,
          goLiveConfirmed: postBy.goLiveConfirmed,
        });
      } else {
        await collabApi.createCollabRequest({
          influencerID: influencerData.id,
          deliverableCounts,
          cash: cashAmount,
          credit: creditAmount,
          campaignId: campaign?.id || null,
          note: isCustomRequest || isUgcCampaignApply ? note : null,
          customCollabType: isCustomRequest ? campaignType : null,
          clientId: client.id,
          isExternalClient: !isInternalClient(client),
          clientName: client.accountName,
          clientEmail: client.email,
          influencerHandle: getInfluencerPrimaryHandle(influencerData),
          prospectiveEmails: client.prospectiveEmails || [],
          proposedSelectedLocationIds: locationIds,
          inboundLinkToken: getInboundLinkToken(client.id),
          proposedContent,
          goLiveConfirmed: postBy.goLiveConfirmed,
        });
      }
  
      if (isMultiStep || isUgcCampaignApply) {
        // These flows end on a dedicated confirmation screen instead of a toast
        setIsSubmitted(true);
        scrollFormToTop();
      } else {
        toast({
          title: requestedCollab ? "Request updated successfully" : "Request sent successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        handleBack();
      }
    } catch (error) {
      if (postBy.handleError(error)) return;
      const notEligible =
        error instanceof Error && error.message === 'AUDIENCE_REQUIREMENTS_NOT_MET';
      toast({
        title: notEligible
          ? "This campaign has creator requirements you don't currently meet"
          : "Error sending request",
        description: notEligible
          ? "The brand limited who can request this campaign."
          : "Please try again later",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!inModal) {
      window.scrollTo(0, 0);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!scrollToCompensation) return;
    // Wait for the sheet's slide-in so the scroll lands on the settled layout
    const timer = setTimeout(() => compensationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 350);
    return () => clearTimeout(timer);
  }, [scrollToCompensation]);

  useEffect(() => {
    if (campaign?.contentRequirements && Object.keys(campaign.contentRequirements).length > 0 && campaign.id) {
      collabApi.downloadInspirationLinks(campaign.id).catch(() => {});
    }
  }, [campaign]);

  useEffect(() => {
    if (requestedCollab && !requestedCollabProp && !toastShown.current) {
      toast({
        title: "Existing Application Found",
        description: "You have already applied for this campaign. You can review and update your application below.",
        status: "info",
        duration: 5000,
        isClosable: true,
      });
      toastShown.current = true;
    }
  }, []); // Only run once when component mounts

  const handleDeleteRequest = async () => {
    if (!requestedCollab) return;
    setIsDeleting(true);
    try {
      await collabApi.deleteCollabRequest(requestedCollab.id);
      toast({
        title: 'Request Deleted',
        description: 'Your collaboration request has been deleted successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setIsDeleteModalOpen(false);
      onExit();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete request. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBack = () => {
    onExit();
  };
  
  // Custom requests branch on creator type (isUgcProposal); rendering the influencer form
  // before influencerData resolves would let a UGC-only creator submit social deliverables
  if (isCustomRequest && isInfluencerDataLoading) {
    return (
      <Box minH="100vh" bg="white" display="flex" alignItems="center" justifyContent="center">
        <Spinner size="lg" color={ff.ink} emptyColor={ff.line} />
      </Box>
    );
  }

  const bannerImage = campaign?.collabImage || client?.bannerPicture;
  const pageTitle = isUgcCampaignApply
    ? 'UGC Campaign'
    : requestedCollab ? 'Update Request' : isUgcProposal ? 'UGC Request' : isCustomRequest ? 'Influencer Request' : 'Review Campaign';
  const hasStickyFooter = isMultiStep || isUgcCampaignApply;
  // An existing UGC application can still change its ask (rate, note) while the brand hasn't answered
  const ugcApplyEdited = !!requestedCollab && (
    cashAmount !== (requestedCollab.proposedInfluencerCash ?? 0) ||
    creditAmount !== (requestedCollab.proposedInfluencerCredit ?? 0) ||
    note !== (requestedCollab.influencerNote ?? '')
  );
  // Same rules as the first Apply (isDisabled) minus "has changes", which counts the note only for custom requests
  const isUpdateDisabled =
    isSubmitting ||
    !!postBy.blockedReason ||
    (!isLocationfulCampaignType(campaign?.campaignType) &&
      !isCollabValid(formValues, requestedCollab, pendingRequests.length, campaign));

  return (
    <>
    <Box ref={formTopRef} minH="100vh" bg="white" color={ff.ink}>

      {/* Hero — image only */}
      <Box
        position="relative"
        h="210px"
        overflow="hidden"
        bg={ff.grey}
        zIndex={0}
      >
        {bannerImage ? (
          <Box
            as="img"
            src={bannerImage}
            alt="Campaign banner"
            w="100%"
            h="100%"
            objectFit="cover"
            style={{ display: 'block' }}
          />
        ) : (
          <Box w="100%" h="100%" bg={ff.grey} />
        )}

        {/* Back button */}
        <Box
          position="absolute"
          top={4}
          left={4}
          w="44px"
          h="44px"
          bg="white"
          borderRadius="full"
          display="flex"
          alignItems="center"
          justifyContent="center"
          cursor="pointer"
          onClick={handleBack}
          _hover={{ bg: ff.grey }}
          zIndex={10}
        >
          <Icon as={ChevronLeftIcon} color={ff.ink} boxSize={6} />
        </Box>

        {/* Support button */}
        <Box position="absolute" top={4} right={4} zIndex={10}>
          <FrostedSupportButton />
        </Box>
      </Box>

      {/* White card slides up over hero */}
      <Box
        bg="white"
        borderTopRadius="28px"
        mt="-32px"
        position="relative"
        zIndex={1}
      >
        {/* Profile pic — absolutely positioned to straddle card top edge */}
        {client?.instagramProfilePicture ? (
          <Box
            as="img"
            src={client.instagramProfilePicture}
            alt={client?.accountName}
            position="absolute"
            top="-32px"
            left="20px"
            w="64px"
            h="64px"
            borderRadius="14px"
            objectFit="cover"
            border="3px solid white"
            style={{ display: 'block' }}
          />
        ) : (
          <Box
            position="absolute"
            top="-32px"
            left="20px"
            w="64px"
            h="64px"
            bg={ff.grey}
            borderRadius="14px"
            border="3px solid white"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={FaCoffee} color={ff.muted} boxSize={5} />
          </Box>
        )}

        {/* Brand name + page label + social icons */}
        <HStack px={5} pt="44px" pb={4} justify="space-between" align="center">
          <VStack align="start" spacing={0} flex={1} mr={3}>
            <Text textStyle="display" fontSize="24px" color={ff.ink} lineHeight="1.2" noOfLines={1}>
              {client?.accountName ?? ''}
            </Text>
            <Text textStyle="eyebrow" color={ff.text2} mt={1}>
              {pageTitle}
            </Text>
          </VStack>
          <HStack spacing={2}>
            {client?.instagramHandle && (
              <Box
                as="button"
                w="44px"
                h="44px"
                flexShrink={0}
                borderRadius="full"
                bg={ff.grey}
                color={ff.ink}
                display="flex"
                alignItems="center"
                justifyContent="center"
                _hover={{ bg: ff.line }}
                transition="background 0.15s"
                onClick={() => window.open(`https://instagram.com/${client!.instagramHandle}`, '_blank')}
              >
                <Icon as={FaInstagram} boxSize={4} />
              </Box>
            )}
            {client?.tiktokHandle && (
              <Box
                as="button"
                w="44px"
                h="44px"
                flexShrink={0}
                borderRadius="full"
                bg={ff.grey}
                color={ff.ink}
                display="flex"
                alignItems="center"
                justifyContent="center"
                _hover={{ bg: ff.line }}
                transition="background 0.15s"
                onClick={() => window.open(`https://tiktok.com/@${client!.tiktokHandle}`, '_blank')}
              >
                <Icon as={FaTiktok} boxSize={4} />
              </Box>
            )}
            {client?.website && (
              <Box
                as="button"
                w="44px"
                h="44px"
                flexShrink={0}
                borderRadius="full"
                bg={ff.grey}
                color={ff.ink}
                display="flex"
                alignItems="center"
                justifyContent="center"
                _hover={{ bg: ff.line }}
                transition="background 0.15s"
                onClick={() => window.open(client!.website, '_blank')}
              >
                <Icon as={FaGlobe} boxSize={4} />
              </Box>
            )}
          </HStack>
        </HStack>

        <Box mx={5} borderTopWidth="1px" borderColor={ff.line} />

        {/* Content */}
        <Box px={5} pt={5} pb={6}>
        {isSubmitted ? (
          <VStack spacing={5} py={10} px={2} textAlign="center">
            <Box
              w="64px"
              h="64px"
              borderRadius="full"
              bg={ff.lime}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={FaCheck} color={ff.ink} boxSize={6} />
            </Box>
            <Box>
              <Text textStyle="display" fontSize="22px" color={ff.ink}>
                {requestedCollab ? 'Request updated!' : 'Request sent!'}
              </Text>
              <Text fontSize="15px" color={ff.text2} mt={2} maxW="300px" mx="auto" lineHeight="1.45">
                {client?.accountName ?? 'The brand'} will review your request. Brands have no deadline
                to accept or decline, so response times vary — we'll notify you the moment they respond.
              </Text>
            </Box>
            <Box w="100%" bg={ff.grey} borderRadius="16px" p={3}>
              <Text fontSize="13px" color={ff.text2}>
                You can update or withdraw your request anytime from your Collabs tab.
              </Text>
            </Box>
            <Button
              variant="ink"
              width="100%"
              onClick={handleBack}
            >
              Done
            </Button>
          </VStack>
        ) : (
        <VStack spacing={4} align="stretch">
        {isMultiStep && (
          <Box>
            <HStack spacing={1.5} mb={3}>
              {STEP_TITLES.map((title, index) => (
                <Box
                  key={title}
                  flex={1}
                  h="4px"
                  borderRadius="full"
                  bg={index <= currentStep ? ff.ink : ff.line}
                  transition="background 0.2s"
                />
              ))}
            </HStack>
            <Text textStyle="eyebrow" color={ff.text2}>
              Step {currentStep + 1} of {STEP_TITLES.length}
            </Text>
            <Text textStyle="display" fontSize="22px" color={ff.ink} mt={1}>
              {STEP_TITLES[currentStep]}
            </Text>
          </Box>
        )}

        {isUgcCampaignApply && campaign && (
          <UgcCampaignApplySummary
            campaign={campaign}
            brandName={client?.accountName ?? 'The brand'}
            locations={campaignLocations}
            isLoadingLocations={isLoadingCampaignLocations}
            selectedLocationIds={proposedSelectedLocationIds}
            onLocationClick={handleLocationClick}
            cashAmount={cashAmount}
            creditAmount={creditAmount}
            onCashChange={handleCashChange}
            onCreditChange={handleCreditChange}
            note={note}
            onNoteChange={handleNoteChange}
            compensationRef={compensationRef}
          />
        )}

        {!isCustomRequest && !isUgcCampaignApply && (
            <CreativeBrief
              collab={requestedCollab ?? generateCollab(campaign)}
              campaign={campaign}
              isRequest={true}
              client={client}
              hideBrandHeader
              selectedLocationIds={proposedSelectedLocationIds}
              onLocationClick={handleLocationClick}
            />
        )}

        {isUgcProposal && showStep(0) && (
          <Box mt={1}>
            <SectionHeading>How you'll collab</SectionHeading>
            <UgcLogisticsPicker
              options={ugcCollabTypeOptions}
              value={campaignType}
              onChange={setCampaignType}
            />
          </Box>
        )}

        {isCustomRequest && !isUgcProposal && clientCampaignTypes.length > 1 && showStep(0) && (
          <Box>
            <SectionHeading>How you'll collab</SectionHeading>
            <Select
              value={campaignType}
              onChange={(e) => setCampaignType(e.target.value as CampaignType)}
            >
              {clientCampaignTypes
                .filter(type => {
                  // If shopify exists, remove shipToHome to avoid duplication
                  if (type === 'shipToHome' && clientCampaignTypes.includes('shopify')) {
                    return false;
                  }
                  return true;
                })
                .map((type) => (
                  <option key={type} value={type}>{campaignTypeDisplayNames[type]}</option>
                ))}
            </Select>
          </Box>
        )}

        {isCustomRequest && isLocationfulCampaignType(campaignType) && showStep(0) && (
          <Box>
            <VStack align="stretch" spacing={3}>
              {!selectedInfluencerLocation ? (
                <>
                  <Text textStyle="eyebrow" color={ff.text2}>
                    Select Locations
                  </Text>
                  <InfluencerLocationPicker
                    onConfirm={(loc) => setSelectedInfluencerLocation(loc)}
                  />
                </>
              ) : (
                <>
                  <LocationList
                    locations={closestLocations}
                    title="Select Locations (choose at least one)"
                    isLoading={isLoadingClientLocations}
                    error={clientLocationsError}
                    isSelectable
                    selectedLocationIds={proposedSelectedLocationIds}
                    onLocationClick={handleLocationClick}
                  />
                  {closestLocations.length > 0 && (
                    <HStack
                      spacing={1.5}
                      cursor="pointer"
                      onClick={() => setSelectedInfluencerLocation(null)}
                      _hover={{ opacity: 0.75 }}
                      transition="opacity 0.15s"
                    >
                      <Icon as={FiMapPin} color={ff.text2} boxSize={3} flexShrink={0} />
                      <Text fontSize="13px" color={ff.text2}>
                        Nearest spots to {selectedInfluencerLocation.zipCode}
                        {' · '}
                        <Text as="span" color={ff.ink} fontWeight={600} textDecoration="underline">Change</Text>
                      </Text>
                    </HStack>
                  )}
                </>
              )}
            </VStack>
          </Box>
        )}

        {isUgcProposal && showStep(0) && (
          <Box mt={1}>
            <SectionHeading right={ugcPiecesSelected > 0 ? `${ugcPiecesSelected} selected` : undefined}>Your deliverables</SectionHeading>
            <UgcDeliverablesPicker
              counts={ugcPresetCounts}
              onChange={setUgcPresetCounts}
              rateByPreset={rateByPreset}
            />
          </Box>
        )}

        {!isUgcProposal && showStep(0) && (!campaign?.contentRequirements || Object.keys(campaign?.contentRequirements || {}).length === 0) && (
          <Box mt={isMultiStep ? 1 : 3}>
            {isMultiStep && <SectionHeading>Your deliverables</SectionHeading>}
            {!isMultiStep && (
              <Text textStyle="eyebrow" color={ff.text2} mb={3}>
                Customize Deliverables
              </Text>
            )}
            <Box
              bg="white"
              borderWidth={1}
              borderColor={ff.line}
              borderRadius="20px"
            >
              <VStack spacing={0} align="stretch" divider={<Box borderTopWidth="1px" borderColor={ff.line} />}>
                {[
                  { label: 'TikTok', count: tikTokCount, setCount: setTikTokCount, channel: 'tiktoks' as SocialChannel },
                  { label: 'IG Reel', count: igReelCount, setCount: setIgReelCount, channel: 'instagramReels' as SocialChannel },
                  { label: 'IG Post', count: igPostCount, setCount: setIgPostCount, channel: 'instagramPosts' as SocialChannel },
                  { label: 'IG Story', count: igStoryCount, setCount: setIgStoryCount, channel: 'instagramStories' as SocialChannel },
                ].map(({ label, count, setCount, channel }) => (
                  <HStack key={label} px={4} py={4} spacing={3} align="center" justify="space-between">
                    <HStack spacing={3} flex={1}>
                      <Box
                        bg={ff.grey}
                        p={2}
                        borderRadius="12px"
                        color={ff.ink}
                        flexShrink={0}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Icon as={getChannelIcon(channel)} boxSize={5} />
                      </Box>
                      <Text fontSize="15px" color={ff.ink} fontWeight={600}>{label}</Text>
                    </HStack>
                    <HStack>
                      <IconButton
                        icon={<FaMinus />}
                        aria-label={`Decrease ${label}`}
                        onClick={() => handleDecrement(setCount, count)}
                        w="44px"
                        minW="44px"
                        h="44px"
                        borderRadius="full"
                        border={`1.5px solid ${ff.ink}`}
                        bg="white"
                        color={ff.ink}
                        _hover={{ bg: ff.grey }}
                        isDisabled={count === 0}
                      />
                      <Text textStyle="display" fontSize="18px" color={ff.ink} minW="20px" textAlign="center">{count}</Text>
                      <IconButton
                        icon={<FaPlus />}
                        aria-label={`Increase ${label}`}
                        onClick={() => handleIncrement(setCount, count)}
                        w="44px"
                        minW="44px"
                        h="44px"
                        borderRadius="full"
                        bg={ff.ink}
                        color={ff.lime}
                        _hover={{ bg: ff.inkSoft }}
                      />
                    </HStack>
                  </HStack>
                ))}
              </VStack>
            </Box>
          </Box>
        )}

        {showStep(0) && (
          <MissingHandleInputs
            missing={missingHandles}
            values={handleInputs}
            onChange={(key, value) => setHandleInputs((prev) => ({ ...prev, [key]: value }))}
          />
        )}

        {showStep(0) && !isUgcCampaignApply && (
        <Box mt={isMultiStep ? 1 : 3} ref={compensationRef} scrollMarginTop="16px">
          {isMultiStep && <SectionHeading>Compensation</SectionHeading>}
          {!isMultiStep && (
            <Text textStyle="eyebrow" color={ff.text2} mb={3}>
              {isCustomRequest ? "Desired Compensation" : "Customize Compensation"}
            </Text>
          )}
          <Box
            bg="white"
            borderWidth={1}
            borderColor={ff.line}
            borderRadius="20px"
            p={4}
          >
            <VStack spacing={4}>
              {(campaignType === 'inStore' || isCustomBrandManaged) && (
                <Box width="100%">
                  <HStack spacing={2.5} mb={2}>
                    <Box
                      bg={ff.grey}
                      p={2}
                      borderRadius="10px"
                      color={ff.ink}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Icon as={FaGift} boxSize={3.5} />
                    </Box>
                    <Text fontSize="15px" fontWeight={600} color={ff.ink}>Credit</Text>
                  </HStack>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Text color={ff.text2} fontWeight={600}>$</Text>
                    </InputLeftElement>
                    <Input
                      value={creditAmount}
                      onChange={handleCreditChange}
                    />
                  </InputGroup>
                </Box>
              )}

              <Box width="100%">
                <HStack spacing={2.5} mb={2} align="center">
                  <Box
                    bg={ff.grey}
                    p={2}
                    borderRadius="10px"
                    color={ff.ink}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Icon as={FaMoneyBillWave} boxSize={3.5} />
                  </Box>
                  <VStack spacing={0} align="start">
                    <Text fontSize="15px" fontWeight={600} color={ff.ink}>Cash</Text>
                    <Text fontSize="13px" color={ff.text2}>
                      Paid after content is verified
                    </Text>
                  </VStack>
                </HStack>
                {isMultiStep ? (
                  // Custom requests: one prominent "Your ask", with where the number came from underneath
                  <>
                    <Text fontSize="13px" color={ff.text2} mb={1}>Your ask</Text>
                    <InputGroup size="lg">
                      <InputLeftElement pointerEvents="none" h="56px">
                        <Text color={ff.muted} fontSize="xl" fontWeight={600}>$</Text>
                      </InputLeftElement>
                      <Input
                        aria-label="Your ask"
                        value={cashAmount}
                        onChange={handleCashChange}
                        h="56px"
                        fontFamily="display"
                        fontSize="2xl"
                        fontWeight={600}
                        borderWidth="1.5px"
                        borderColor={ff.ink}
                      />
                      <InputRightElement pointerEvents="none" h="56px">
                        <Icon as={FiEdit2} color={ff.muted} boxSize={4} />
                      </InputRightElement>
                    </InputGroup>
                    {(() => {
                      const hints = [
                        isUgcProposal && !cashEdited && suggestedUgcCash > 0 ? 'Suggested from your rates.' : null,
                        !isCustomBrandManaged && ['retail', 'shopify', 'shipToHome', 'brandManaged'].includes(campaignType)
                          ? 'This is in addition to your gifted product.'
                          : null,
                      ].filter(Boolean);
                      return hints.length > 0 && (
                        <Text fontSize="13px" color={ff.text2} mt={2}>{hints.join(' ')}</Text>
                      );
                    })()}
                  </>
                ) : (
                  <>
                    {!isCustomBrandManaged &&
                      (campaignType === 'retail' ||
                        campaignType === 'shopify' ||
                        campaignType === 'shipToHome' ||
                        campaignType === 'brandManaged') && (
                        <Text fontSize="13px" color={ff.text2} mb={2}>
                          This is in addition to your gifted product.
                        </Text>
                      )}
                    <InputGroup>
                      <InputLeftElement pointerEvents="none">
                        <Text color={ff.text2} fontWeight={600}>$</Text>
                      </InputLeftElement>
                      <Input
                        value={cashAmount}
                        onChange={handleCashChange}
                      />
                    </InputGroup>
                  </>
                )}
              </Box>
            </VStack>
          </Box>
        </Box>
        )}

        {isMultiStep && currentStep === REVIEW_STEP && (
          <ProposalReviewSummary
            campaignType={campaignType}
            ugcPresetCounts={isUgcProposal ? ugcPresetCounts : undefined}
            socialDeliverables={!isUgcProposal ? [
              { label: 'TikTok', count: tikTokCount },
              { label: 'IG Reel', count: igReelCount },
              { label: 'IG Post', count: igPostCount },
              { label: 'IG Story', count: igStoryCount },
            ] : undefined}
            selectedLocations={(clientLocations ?? []).filter(location => proposedSelectedLocationIds.includes(location.docId))}
            cashAmount={cashAmount}
            creditAmount={creditAmount}
            showCredit={campaignType === 'inStore' || isCustomBrandManaged}
            onEditStep={() => goToStep(0)}
          />
        )}

        {isCustomRequest && showStep(REVIEW_STEP) && (
          <Box mt={1}>
            <Text textStyle="eyebrow" color={ff.text2} mb={3}>
              Additional Notes
            </Text>
            <Box
              bg="white"
              borderWidth={1}
              borderColor={ff.line}
              borderRadius="20px"
              p={4}
            >
              {(campaignType === "retail" || campaignType === "shipToHome" || campaignType === "shopify") && (
                <Text fontSize="14px" color={ff.text2} mb={3}>
                  Optionally include the product(s) you are looking to receive in this collab.
                </Text>
              )}
              <Textarea
                value={note}
                onChange={handleNoteChange}
                placeholder="Describe what you're looking for in this collaboration..."
                minH="120px"
              />
            </Box>
          </Box>
        )}

        {showStep(REVIEW_STEP) && (
          <Text fontSize="15px" color={ff.ink} mb={3}>
            {(!requestedCollab && pendingRequests.length >= SINGLE_CLIENT_COLLAB_REQUEST_LIMIT) ? 'You have reached the maximum number of collab invites for this brand. Please wait for your previous request to be approved or denied before requesting a new collab.' : ''}
          </Text>
        )}

        {showStep(REVIEW_STEP) && <PostByConfirm state={postBy} />}

        <Box
          position={hasStickyFooter ? 'sticky' : 'static'}
          bottom={0}
          zIndex={5}
          mx={hasStickyFooter ? -5 : 0}
          px={hasStickyFooter ? 5 : 0}
          pt={hasStickyFooter ? 3 : 0}
          pb={hasStickyFooter ? 'max(12px, env(safe-area-inset-bottom))' : 0}
          bg={hasStickyFooter ? 'rgba(255,255,255,0.92)' : undefined}
          backdropFilter={hasStickyFooter ? 'blur(8px)' : undefined}
          borderTopWidth={hasStickyFooter ? '1px' : 0}
          borderTopColor={ff.line}
        >
        <VStack spacing={2} align="stretch">
        {isUgcCampaignApply ? (
          // UGC campaigns: the terms are the campaign's, so it's just Apply (or withdraw an application)
          <>
            {requestedCollab ? (
              <>
                <Text fontSize="14px" color={ff.text2} textAlign="center">
                  You've applied to this campaign. {client?.accountName ?? 'The brand'} will review it.
                </Text>
                {ugcApplyEdited && (
                  <Button
                    variant="ink"
                    onClick={handleSendRequest}
                    isDisabled={isUpdateDisabled}
                    isLoading={isSubmitting}
                  >
                    Update request
                  </Button>
                )}
              </>
            ) : (
              <Button
                variant="ink"
                onClick={handleSendRequest}
                isDisabled={isDisabled()}
                isLoading={isSubmitting}
              >
                {cashAmount !== (campaign?.cash ?? 0) ? `Apply at $${cashAmount}` : 'Apply'}
              </Button>
            )}
            <Button variant="ghost" h="48px" borderRadius="full" color={ff.text2} fontWeight={600} onClick={handleBack}>
              Back
            </Button>
          </>
        ) : isMultiStep && currentStep < REVIEW_STEP ? (
          <>
            {currentStepBlocker && (
              <Text fontSize="13px" color={ff.text2} textAlign="center">
                {currentStepBlocker}
              </Text>
            )}
            <Button
              variant="ink"
              isDisabled={!!currentStepBlocker}
              onClick={() => goToStep(currentStep + 1)}
            >
              Continue
            </Button>
            {currentStep > 0 && (
              <Button
                variant="ghost"
                h="48px"
                borderRadius="full"
                color={ff.text2}
                fontWeight={600}
                onClick={() => goToStep(currentStep - 1)}
              >
                Back
              </Button>
            )}
          </>
        ) : (
          <>
            <Button
              variant="ink"
              onClick={handleSendRequest}
              isDisabled={isDisabled()}
              isLoading={isSubmitting}
            >
              {requestedCollab ? "Update Request" : isMultiStep ? "Submit Request" : "Send Request"}
            </Button>
            {isMultiStep && (
              <Button
                variant="ghost"
                h="48px"
                borderRadius="full"
                color={ff.text2}
                fontWeight={600}
                onClick={() => goToStep(0)}
              >
                Back
              </Button>
            )}
          </>
        )}
        {/* Withdrawing needs no wizard progress */}
        {requestedCollab && (
          <Button
            variant="inkOutline"
            color={ff.danger}
            borderColor={ff.danger}
            onClick={() => setIsDeleteModalOpen(true)}
          >
            Delete Request
          </Button>
        )}
        </VStack>
        </Box>
      </VStack>
        )}
        </Box>
      </Box>
    </Box>

      <DeleteRequestModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteRequest}
        isLoading={isDeleting}
        clientName={client?.accountName}
      />
    </>
  );
};

export default RequestCollabForm;
